import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { getLocalMonthStart, getLocalMonthEnd } from '@/lib/timezoneUtils';
import { getDealSalesAmount } from '@/utils/salesAmount';

export interface USTeamSalesItem {
  userId: string;
  userName: string;
  avatarUrl: string | null;
  totalSales: number;      // Vendido
  totalReceived: number;   // Recebido (NEW)
  goalAmount: number;
  goalProgress: number;    // % da meta (0-100+)
  dealsCount: number;
  rank: number;
}

interface UseUSTeamSalesRankingOptions {
  year: number;
  month: number;
  businessUnit?: string;
}

export function useUSTeamSalesRanking(options?: UseUSTeamSalesRankingOptions) {
  const now = new Date();
  const year = options?.year ?? now.getFullYear();
  const month = options?.month ?? (now.getMonth() + 1); // 1-12
  const businessUnit = options?.businessUnit ?? 'us';

  // Build date range for selected month/year
  const selectedDate = new Date(year, month - 1, 1);
  const monthStart = getLocalMonthStart(selectedDate);
  const monthEnd = getLocalMonthEnd(selectedDate);

  return useQuery({
    queryKey: ['us-team-sales-ranking', year, month, businessUnit],
    queryFn: async (): Promise<USTeamSalesItem[]> => {
      // Format as YYYY-MM-DD using local date components
      const formatDateOnly = (d: Date) => {
        const y = d.getFullYear();
        const m = String(d.getMonth() + 1).padStart(2, '0');
        const day = String(d.getDate()).padStart(2, '0');
        return `${y}-${m}-${day}`;
      };
      const startDate = formatDateOnly(monthStart);
      const endDate = formatDateOnly(monthEnd);

      // 1. Fetch WON deals via RPC with business_unit filter
      const { data: wonDeals, error: dealsError } = await supabase
        .rpc('get_sales_ranking_deals', {
          p_start_date: startDate,
          p_end_date: endDate,
          p_business_unit: businessUnit
        });

      if (dealsError) {
        console.error('Error fetching won deals via RPC:', dealsError);
        throw dealsError;
      }

      // 2. Aggregate sales by consultant
      const salesByUser = new Map<string, { total: number; received: number; count: number }>();
      
      wonDeals?.forEach(deal => {
        if (!deal.sales_consultant) return;

        const d = deal as any;
        const effectiveAmount = getDealSalesAmount({
          deal_status: d.deal_status === 'cancelled' ? 'cancelled' : 'active',
          closed_date: d.closed_date,
          cancelled_at: d.cancelled_at,
          total_amount: d.total_amount,
          amount_received: d.amount_received ?? 0,
          upsell_credit_amount: d.upsell_credit_amount ?? 0,
        });
        
        const current = salesByUser.get(deal.sales_consultant) || { total: 0, received: 0, count: 0 };
        salesByUser.set(deal.sales_consultant, {
          total: current.total + effectiveAmount,
          received: current.received + (d.amount_received ?? 0),
          count: current.count + 1,
        });
      });

      if (salesByUser.size === 0) {
        // Still fetch users with goals for this BU to show them with 0 sales
        const { data: goalsOnly } = await supabase
          .from('user_sales_goals')
          .select('user_id, goal_amount')
          .eq('year', year)
          .eq('month', month)
          .eq('business_unit', businessUnit);

        if (!goalsOnly?.length) return [];

        const userIds = goalsOnly.map(g => g.user_id);
        const { data: users } = await supabase
          .from('app_users')
          .select('id, first_name, last_name, display_name, avatar_url')
          .in('id', userIds);

        const usersMap = new Map(users?.map(u => [u.id, u]) || []);

        return goalsOnly.map((g, index) => {
          const user = usersMap.get(g.user_id);
          return {
            userId: g.user_id,
            userName: user?.display_name || 
                      `${user?.first_name || ''} ${user?.last_name || ''}`.trim() || 
                      'Unknown',
            avatarUrl: user?.avatar_url || null,
            totalSales: 0,
            totalReceived: 0,
            goalAmount: g.goal_amount || 0,
            goalProgress: 0,
            dealsCount: 0,
            rank: index + 1,
          };
        });
      }

      // 3. Fetch user info
      const userIds = Array.from(salesByUser.keys());
      
      const { data: users, error: usersError } = await supabase
        .from('app_users')
        .select('id, first_name, last_name, display_name, avatar_url')
        .in('id', userIds);

      if (usersError) {
        console.error('Error fetching users:', usersError);
        throw usersError;
      }

      // 4. Fetch goals via RPC (bypasses RLS for TV display)
      const { data: goals, error: goalsError } = await supabase
        .rpc('get_team_sales_goals', {
          p_year: year,
          p_month: month,
          p_business_unit: businessUnit
        });

      if (goalsError) {
        console.error('Error fetching goals:', goalsError);
        throw goalsError;
      }

      const goalsMap = new Map(goals?.map(g => [g.user_id, g.goal_amount]) || []);
      const usersMap = new Map(users?.map(u => [u.id, u]) || []);

      // 5. Build ranking
      const ranking: USTeamSalesItem[] = Array.from(salesByUser.entries())
        .map(([userId, data]) => {
          const user = usersMap.get(userId);
          const goalAmount = goalsMap.get(userId) || 0;
          const goalProgress = goalAmount > 0 ? (data.total / goalAmount) * 100 : 0;

          return {
            userId,
            userName: user?.display_name || 
                      `${user?.first_name || ''} ${user?.last_name || ''}`.trim() || 
                      'Unknown',
            avatarUrl: user?.avatar_url || null,
            totalSales: data.total,
            totalReceived: data.received,
            goalAmount,
            goalProgress,
            dealsCount: data.count,
            rank: 0,
          };
        })
        .sort((a, b) => b.totalSales - a.totalSales);

      // 6. Assign ranks
      ranking.forEach((item, index) => {
        item.rank = index + 1;
      });

      return ranking;
    },
    refetchInterval: 60000, // 1 minuto - otimizado para TV
    staleTime: 30000,
    retry: 3,
    retryDelay: 2000,
  });
}
